<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { onClickOutside } from '@vueuse/core';

import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const emit = defineEmits(['updateTemperature']);

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const temperatureMenuRef = ref(null);
const isTemperatureMenuOpen = ref(false);

const contact = computed(() => props.conversation.contact || {});
const contactAttributes = computed(() => contact.value.custom_attributes || {});
const leadScore = computed(
  () => Number(contactAttributes.value.lead_score) || 0
);
const messagesCount = computed(
  () => Number(props.conversation.messages_count) || 0
);
const temperatureOptions = ['quente', 'morno', 'frio'];
const temperatureLabels = computed(() => ({
  quente: t('KANBAN.TEMPERATURE.QUENTE'),
  morno: t('KANBAN.TEMPERATURE.MORNO'),
  frio: t('KANBAN.TEMPERATURE.FRIO'),
}));
const temperature = computed(() => {
  const value = contactAttributes.value.temperatura;
  return ['quente', 'morno', 'frio'].includes(value) ? value : 'frio';
});
const temperatureEmoji = computed(
  () => ({ quente: '🔥', morno: '☀️', frio: '❄️' })[temperature.value]
);
const temperatureLabel = computed(
  () => temperatureLabels.value[temperature.value]
);
const temperatureMenuItems = computed(() =>
  temperatureOptions.map(option => ({
    label: temperatureLabels.value[option],
    value: option,
    action: 'selectTemperature',
    isSelected: option === temperature.value,
  }))
);
const scoreClasses = computed(() => {
  if (leadScore.value >= 70) return 'bg-n-teal-3 text-n-teal-11';
  if (leadScore.value >= 40) return 'bg-n-amber-3 text-n-amber-11';
  return 'bg-n-ruby-3 text-n-ruby-11';
});
const lastActivity = computed(() => {
  if (!props.conversation.last_activity_at) return t('KANBAN.CARD.NO_ACTIVITY');

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(props.conversation.last_activity_at));
});
const contactName = computed(
  () => contact.value.name || t('KANBAN.CARD.UNNAMED')
);

const onDragStart = event => {
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', String(props.conversation.id));
};

const openConversation = () => {
  router.push(
    accountScopedRoute('inbox_conversation', {
      conversation_id: props.conversation.id,
    })
  );
};

const toggleTemperatureMenu = () => {
  isTemperatureMenuOpen.value = !isTemperatureMenuOpen.value;
};

const onTemperatureAction = ({ action, value }) => {
  isTemperatureMenuOpen.value = false;
  if (action !== 'selectTemperature' || value === temperature.value) return;

  emit('updateTemperature', {
    contactId: contact.value.id,
    temperature: value,
  });
};

onClickOutside(temperatureMenuRef, () => {
  isTemperatureMenuOpen.value = false;
});
</script>

<template>
  <article
    class="p-4 rounded-xl border cursor-pointer bg-n-solid-1 border-n-weak transition-colors duration-150 hover:border-n-strong hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
    draggable="true"
    role="button"
    tabindex="0"
    :aria-label="contactName"
    @dragstart="onDragStart"
    @click="openConversation"
    @keydown.enter.prevent="openConversation"
    @keydown.space.prevent="openConversation"
  >
    <div class="flex gap-3 justify-between items-start">
      <div class="min-w-0">
        <h3 class="font-medium truncate text-n-slate-12">
          {{ contactName }}
        </h3>
        <p class="mt-0.5 text-xs truncate text-n-slate-10">
          {{ contact.phone_number || t('KANBAN.CARD.NO_PHONE') }}
        </p>
      </div>
      <div class="flex gap-1.5 items-center shrink-0">
        <div
          ref="temperatureMenuRef"
          class="relative flex items-center"
          draggable="false"
          @click.stop
        >
          <button
            type="button"
            class="inline-flex items-center gap-0.5 px-1 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :title="temperatureLabel"
            :aria-label="t('KANBAN.TEMPERATURE.CHANGE')"
            aria-haspopup="menu"
            :aria-expanded="isTemperatureMenuOpen"
            @click="toggleTemperatureMenu"
            @keydown.enter.stop.prevent="toggleTemperatureMenu"
            @keydown.space.stop.prevent="toggleTemperatureMenu"
          >
            <span aria-hidden="true">{{ temperatureEmoji }}</span>
            <Icon icon="i-lucide-chevron-down" class="size-3" />
          </button>
          <DropdownMenu
            v-if="isTemperatureMenuOpen"
            class="top-full mt-1 ltr:right-0 rtl:left-0"
            :menu-items="temperatureMenuItems"
            @action="onTemperatureAction"
          />
        </div>
        <span
          class="px-2 py-0.5 text-xs font-semibold rounded-full"
          :class="scoreClasses"
        >
          {{ leadScore }}
        </span>
      </div>
    </div>

    <p
      v-if="contactAttributes.servico_interesse"
      class="mt-3 text-sm line-clamp-2 text-n-slate-11"
    >
      {{ contactAttributes.servico_interesse }}
    </p>

    <div
      class="flex gap-2 justify-between items-center pt-3 mt-3 border-t border-n-weak"
    >
      <span class="flex gap-1 items-center text-xs text-n-slate-10">
        <Icon icon="i-lucide-message-circle" class="size-3.5" />
        {{ t('KANBAN.CARD.MESSAGE_COUNT', messagesCount) }}
      </span>
      <span class="text-xs text-n-slate-10">{{ lastActivity }}</span>
    </div>
  </article>
</template>
